/**
 * Featured products slider – Tech Hunk Best Sellers carousel
 * Scrolls the track (same pattern as cards-carousel); arrow nav + card click.
 */
(function () {
  const SECTION_SELECTOR = '.featured-products-slider[data-section-id]';

  function getScrollTrack(section) {
    return section.querySelector('.featured-products-slider__track.is-scroll');
  }

  function stepSize(track) {
    const card = track.querySelector('.featured-products-slider__card');
    if (!card) return 0;
    const gap = parseFloat(getComputedStyle(track).gap || '0', 10);
    return card.offsetWidth + gap;
  }

  function updateArrows(section, track) {
    const prev = section.querySelector('.featured-products-slider__arrow--prev');
    const next = section.querySelector('.featured-products-slider__arrow--next');
    if (!prev || !next || !track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const left = track.scrollLeft;
    const threshold = 4;

    prev.disabled = left <= threshold;
    next.disabled = left >= maxScroll - threshold;
  }

  function scrollTrack(track, direction) {
    const step = stepSize(track);
    if (!step) return;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  function bindCardClicks(root) {
    root.querySelectorAll('.featured-products-slider__card').forEach((card) => {
      if (card.dataset.fpsCardBound === 'true') return;

      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button')) return;
        const href = card.getAttribute('data-href');
        if (href) window.location.assign(href);
      });

      card.dataset.fpsCardBound = 'true';
    });
  }

  // Optional autoplay (used by the product page's "You may also like" carousel; opt-in via
  // data-autoplay="true" so the homepage slider's behavior is unchanged unless it asks for it too).
  function initAutoplay(section, track) {
    if (section.dataset.fpsAutoplayBound === 'true') return;
    if (section.dataset.autoplay !== 'true') return;
    if (track.scrollWidth <= track.clientWidth + 4) return; // nothing to scroll

    section.dataset.fpsAutoplayBound = 'true';
    const interval = Math.max(2, parseFloat(section.dataset.autoplayInterval || '4')) * 1000;
    let timer = null;
    let resumeTimer = null;

    function tick() {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (track.scrollLeft >= maxScroll - 4) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollTrack(track, 1);
      }
    }

    function play() {
      stop();
      timer = setInterval(tick, interval);
    }
    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }
    // Pausing on any manual interaction (hover, touch, wheel, arrow click) so autoplay never
    // fights the shopper mid-swipe; resumes on its own a little after they let go.
    function pauseThenResume() {
      stop();
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(play, interval * 1.5);
    }

    section.addEventListener('mouseenter', stop);
    section.addEventListener('mouseleave', play);
    track.addEventListener('touchstart', pauseThenResume, { passive: true });
    track.addEventListener('wheel', pauseThenResume, { passive: true });
    section.querySelectorAll('.featured-products-slider__arrow').forEach((btn) => {
      btn.addEventListener('click', pauseThenResume);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else play();
    });

    play();
  }

  function initSection(section) {
    const track = getScrollTrack(section);
    const prev = section.querySelector('.featured-products-slider__arrow--prev');
    const next = section.querySelector('.featured-products-slider__arrow--next');

    if (section.dataset.fpsBound === 'true') return;

    bindCardClicks(section);

    if (track) {
      const onScroll = () => updateArrows(section, track);
      track.addEventListener('scroll', onScroll, { passive: true });

      if (prev) {
        prev.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollTrack(track, -1);
        });
      }

      if (next) {
        next.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollTrack(track, 1);
        });
      }

      window.addEventListener(
        'resize',
        () => {
          updateArrows(section, track);
        },
        { passive: true }
      );

      const imgs = track.querySelectorAll('img');
      let pending = 0;
      const refresh = () => updateArrows(section, track);

      imgs.forEach((img) => {
        if (img.complete) return;
        pending += 1;
        img.addEventListener('load', () => {
          pending -= 1;
          if (pending === 0) refresh();
        }, { once: true });
      });

      refresh();
      requestAnimationFrame(refresh);
      initAutoplay(section, track);
    }

    section.dataset.fpsBound = 'true';
  }

  // Sections that load their cards later via fetch (e.g. product recommendations, which only
  // populate once Shopify's async call returns) need a re-init once that content actually lands.
  function watchDynamicSections() {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (section.dataset.fpsWatching === 'true' || !section.matches('product-recommendations')) return;
      section.dataset.fpsWatching = 'true';
      const observer = new MutationObserver(() => {
        delete section.dataset.fpsBound;
        initSection(section);
      });
      observer.observe(section, { childList: true });
    });
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
    document.querySelectorAll('[data-fps-grid]').forEach((grid) => {
      if (grid.matches(SECTION_SELECTOR)) return;
      bindCardClicks(grid);
    });
    watchDynamicSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section =
      event.target.querySelector(SECTION_SELECTOR) ||
      event.target.closest(SECTION_SELECTOR);
    if (section) {
      delete section.dataset.fpsBound;
      initSection(section);
    }
  });
})();
